/**
 * ExecutiveOverviewV2 — Power-BI-style presentation shell for the
 * Admin Dashboard Overview. **Presentation layer ONLY** — every
 * incoming prop / value is consumed verbatim from the existing
 * `buildOverviewMetrics()`, `mtdData`, `ytdData`, `dailyData`,
 * `costEfficiency`, and `_mtdProjectionSeries` outputs already used
 * by AdminDashboard.js. NO calculations, NO API calls, NO logic
 * changes live here.
 *
 * Sections:
 *   1. HeroKpiRow — 3 tiles (Total Revenue · Projected Close · YTD)
 *   2. RevenuePerformance — MTD chart + compact summary table
 *   3. FinancialSnapshot — APC · FC% · BC% · Energy in one table
 *   4. RoomsAccommodation — matrix table + Actual-vs-Budget chart
 *   5. OutletPulseTable — one row per outlet
 *   6. UtilitiesEnergyBlock — table + small bar chart
 */
import { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, Bar, BarChart,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from 'recharts';
import { ArrowUpRight, ArrowDownRight, Activity, Zap } from 'lucide-react';

// ─────────────────────── shared formatters (verbatim copy) ────────────────────
const compactINR = (n) => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  const abs = Math.abs(v);
  const s = v < 0 ? '-' : '';
  if (abs >= 1e7) return `${s}₹${(abs / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${s}₹${(abs / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `${s}₹${(abs / 1e3).toFixed(1)}K`;
  return `${s}₹${abs.toFixed(0)}`;
};
const pctFmt = (v, opts = {}) => {
  if (v == null || isNaN(v)) return '—';
  const s = opts.signed && v >= 0 ? '+' : '';
  return `${s}${Number(v).toFixed(1)}%`;
};

// Semantic colour tokens for variance — used consistently everywhere.
const goodBg = 'bg-emerald-50 text-emerald-700';
const badBg  = 'bg-rose-50 text-rose-700';
const goodTxt = 'text-emerald-700';
const badTxt  = 'text-rose-700';
const mutedTxt = 'text-[#7A8B82]';

const Section = ({ eyebrow, title, right, children, testId }) => (
  <section className="mb-6" data-testid={testId}>
    {title && (
      <header className="flex items-center justify-between mb-3 rounded-md bg-[color:var(--color-primary)] text-white px-4 h-11">
        <h2 className="text-[14px] font-semibold text-white leading-none truncate">{title}</h2>
        {right && <div className="text-[11px] text-white/85 shrink-0 pl-3">{right}</div>}
      </header>
    )}
    {children}
  </section>
);

// ─────────────────────── hand-rolled SVG sparkline ────────────────────
export function Sparkline({ data, width = 120, height = 32, color = '#2D6A4F', showZeroLine = false }) {
  const points = useMemo(() => {
    if (!Array.isArray(data) || data.length < 2) return null;
    const nums = data.map((v) => (v == null || isNaN(v) ? 0 : Number(v)));
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    const span = max - min || 1;
    const stepX = width / (nums.length - 1);
    return nums.map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / span) * (height - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  }, [data, width, height]);
  if (!points) {
    return <div className="text-[10px] text-[#C7D0CB] italic h-[32px] flex items-center">no trend</div>;
  }
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
         className="overflow-visible" aria-hidden>
      {showZeroLine && (
        <line x1={0} y1={height / 2} x2={width} y2={height / 2}
              stroke="#EEF2EF" strokeDasharray="2 2" />
      )}
      <polyline fill="none" stroke={color} strokeWidth={1.75} strokeLinejoin="round"
                strokeLinecap="round" points={points} />
    </svg>
  );
}

// ─────────────────────── hero cards ────────────────────
export function HeroKpiRow({
  totalRevenue, totalBudget, totalVarAmt, totalVarPct,
  fbSales, roomRev, hasRooms, revenueSparkline,
  projectedClose, projectedBudget, projectedVarPct, projectionAvailable, projectionMsg,
  ytdActual, ytdBudget, ytdVarAmt, ytdVarPct, ytdSparkline,
  testIdPrefix = 'exec-hero',
  onTotalRevenueClick,
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid={`${testIdPrefix}-row`}>
      <HeroTile
        testId={`${testIdPrefix}-revenue`}
        eyebrow="Total Revenue"
        value={compactINR(totalRevenue)}
        onClick={onTotalRevenueClick}
        secondary={
          <>
            <span className={mutedTxt}>Budget </span>
            <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{compactINR(totalBudget)}</span>
            <span className="mx-1.5 text-[#C7D0CB]">·</span>
            <VarianceChip amount={totalVarAmt} pct={totalVarPct} compact />
          </>
        }
        footer={hasRooms && (
          <>
            <span className={mutedTxt}>F&amp;B </span>
            <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{compactINR(fbSales)}</span>
            <span className="mx-2 text-[#C7D0CB]">+ Rooms </span>
            <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{compactINR(roomRev)}</span>
          </>
        )}
        sparkline={revenueSparkline}
        varUp={totalVarAmt >= 0}
      />

      <HeroTile
        testId={`${testIdPrefix}-projected`}
        eyebrow="Projected Close"
        value={projectionAvailable ? compactINR(projectedClose) : '—'}
        secondary={
          projectionAvailable ? (
            <>
              <span className={mutedTxt}>Full-Month Budget </span>
              <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{compactINR(projectedBudget)}</span>
              {projectedVarPct != null && (
                <>
                  <span className="mx-1.5 text-[#C7D0CB]">·</span>
                  <span className={`tabular-nums font-medium ${projectedVarPct >= 0 ? goodTxt : badTxt}`}>
                    {pctFmt(projectedVarPct, { signed: true })} vs Budget
                  </span>
                </>
              )}
            </>
          ) : (
            <span className="italic text-[#9CA3AF]">{projectionMsg || 'Projection unavailable'}</span>
          )
        }
        footer={null}
        sparkline={null}
        varUp={projectedVarPct == null ? null : projectedVarPct >= 0}
      />

      <HeroTile
        testId={`${testIdPrefix}-ytd`}
        eyebrow="Current Year YTD Revenue"
        value={compactINR(ytdActual)}
        secondary={
          <>
            <span className={mutedTxt}>Budget </span>
            <span className="tabular-nums font-medium text-[color:var(--color-text-primary)]">{compactINR(ytdBudget)}</span>
            <span className="mx-1.5 text-[#C7D0CB]">·</span>
            <VarianceChip amount={ytdVarAmt} pct={ytdVarPct} compact />
          </>
        }
        footer={null}
        sparkline={ytdSparkline}
        varUp={ytdVarAmt >= 0}
      />
    </div>
  );
}

function HeroTile({ eyebrow, value, secondary, footer, sparkline, testId, varUp, onClick }) {
  const clickable = typeof onClick === 'function';
  const interactionProps = clickable ? {
    role: 'button',
    tabIndex: 0,
    onClick,
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } },
    'aria-label': `Open ${eyebrow} outlet drilldown`,
  } : {};
  return (
    <div data-testid={testId} {...interactionProps}
         className={`bg-white border border-[#E2E8DF] rounded-xl overflow-hidden flex flex-col min-h-[130px] ${
           clickable ? 'cursor-pointer hover:border-[color:var(--color-primary)] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)] transition-shadow' : ''
         }`}>
      <div className="bg-[color:var(--color-primary)] text-white px-5 py-2 flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold">
          {eyebrow}
        </div>
        {sparkline && (
          <Sparkline data={sparkline} color={varUp === false ? '#FCA5A5' : '#B6E7C9'} />
        )}
      </div>
      <div className="px-5 pt-3 pb-4 flex-1 flex flex-col justify-between">
        <div>
          <div className="text-[32px] leading-none font-bold text-[color:var(--color-text-primary)] tabular-nums">
            {value}
          </div>
          {secondary && (
            <div className="text-[11.5px] text-[color:var(--color-text-muted)] mt-2 leading-relaxed">
              {secondary}
            </div>
          )}
        </div>
        {footer && (
          <div className="text-[11px] mt-2 pt-2 border-t border-[#F0F4F1]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function VarianceChip({ amount, pct, compact = false }) {
  if (amount == null && pct == null) return <span className={`${mutedTxt} italic`}>no benchmark</span>;
  const up = amount != null ? amount >= 0 : (pct >= 0);
  const cls = up ? goodTxt : badTxt;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const pctPart = pct != null ? pctFmt(pct, { signed: true }) : null;
  const amtPart = amount != null ? `${up ? '+' : ''}${compactINR(amount)}` : null;
  return (
    <span className={`inline-flex items-baseline gap-1 tabular-nums font-medium ${cls}`}>
      {!compact && <Icon className="w-3 h-3 self-center" strokeWidth={2.5} />}
      {pctPart}
      {amtPart && <span className={`${mutedTxt} font-normal ml-0.5`}>({amtPart})</span>}
    </span>
  );
}

// ─────────────────────── Section 2: Revenue Performance ────────────────────
export function RevenuePerformanceSection({
  mtdSeries, projectedTotal, budgetToDate, mtdActual, fullMonthBudget,
  fullMonthBudgetAvailable, ytdActual, ytdBudget,
  periodLabel, testId = 'revenue-performance',
}) {
  const mtdVar = mtdActual - budgetToDate;
  const mtdVarPct = budgetToDate > 0 ? (mtdVar / budgetToDate) * 100 : null;
  const ytdVar = ytdActual - ytdBudget;
  const ytdVarPct = ytdBudget > 0 ? (ytdVar / ytdBudget) * 100 : null;
  const projVarPct = (projectedTotal != null && fullMonthBudget > 0)
    ? ((projectedTotal - fullMonthBudget) / fullMonthBudget) * 100 : null;
  const achPct = (fullMonthBudget > 0 && projectedTotal != null)
    ? (projectedTotal / fullMonthBudget) * 100 : null;

  return (
    <Section testId={testId} eyebrow="Section 02" title="Revenue Performance"
             right={periodLabel && <span className="text-[11px] text-[#7A8B82]">{periodLabel}</span>}>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white border border-[#E2E8DF] rounded-xl overflow-hidden">
          <div className="bg-[color:var(--color-primary)] text-white px-4 py-2 text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5">
            <Activity className="w-3 h-3" strokeWidth={2.5} /> MTD Cumulative — Actual vs Budget vs Projection
          </div>
          <div className="h-[280px] p-3">
            {Array.isArray(mtdSeries) && mtdSeries.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={mtdSeries} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#EEF2EF" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: '#7A8B82', fontSize: 10 }}
                          axisLine={{ stroke: '#EEF2EF' }} tickLine={false} />
                  <YAxis tickFormatter={(v) => compactINR(v).replace('₹', '')}
                          tick={{ fill: '#7A8B82', fontSize: 10 }}
                          axisLine={false} tickLine={false} width={54} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 6,
                                     borderColor: '#E2E8DF', boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}
                    formatter={(v) => compactINR(v)}
                    labelFormatter={(l) => `Day ${l}`} />
                  <Legend iconType="line" wrapperStyle={{ fontSize: 10, paddingTop: 6 }}
                           iconSize={12} />
                  <Area type="monotone" dataKey="actualCum" name="Cumulative Actual"
                         stroke="#2D6A4F" strokeWidth={2} fillOpacity={0.08} fill="#2D6A4F" dot={false} />
                  <Line type="monotone" dataKey="budgetCum" name="Budget Pace"
                         stroke="#7A8B82" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                  <Line type="monotone" dataKey="projectedCum" name="Projection"
                         stroke="#B48E00" strokeWidth={1.5} strokeDasharray="2 3" dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-[12px] text-[#9CA3AF] italic">
                No trajectory data available
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-[#E2E8DF] rounded-xl p-0 overflow-hidden">
          <div className="bg-[color:var(--color-primary)] text-white px-4 py-2 text-[11px] uppercase tracking-widest font-semibold">
            Revenue Summary
          </div>
          <table className="w-full text-[12px]">
            <tbody className="divide-y divide-[#F0F4F1]">
              <RevRow label="MTD Actual"   value={compactINR(mtdActual)} />
              <RevRow label="MTD Budget"   value={compactINR(budgetToDate)} />
              <RevRow label="MTD Variance" value={<VarianceChip amount={mtdVar} pct={mtdVarPct} compact />} />
              <RevRow label="Projected Close"
                       value={projectedTotal != null ? compactINR(projectedTotal) : <span className="italic text-[#9CA3AF]">—</span>} />
              <RevRow label="Full-Month Budget"
                       value={fullMonthBudgetAvailable ? compactINR(fullMonthBudget)
                                                          : <span className="italic text-[#9CA3AF]">Unavailable</span>} />
              <RevRow label="Projection vs Budget"
                       value={projVarPct != null
                         ? <span className={projVarPct >= 0 ? goodTxt : badTxt}>{pctFmt(projVarPct, { signed: true })}</span>
                         : <span className="italic text-[#9CA3AF]">—</span>} />
              <RevRow label="YTD Actual"   value={compactINR(ytdActual)} />
              <RevRow label="YTD Budget"   value={compactINR(ytdBudget)} />
              <RevRow label="YTD Variance" value={<VarianceChip amount={ytdVar} pct={ytdVarPct} compact />} />
              <RevRow label="Achievement" value={
                achPct != null
                  ? <span className={achPct >= 100 ? goodTxt : (achPct >= 90 ? 'text-amber-700' : badTxt)}>
                      {achPct.toFixed(1)}%
                    </span>
                  : <span className="italic text-[#9CA3AF]">—</span>} bold />
            </tbody>
          </table>
        </div>
      </div>
    </Section>
  );
}

function RevRow({ label, value, bold = false }) {
  return (
    <tr className="hover:bg-[#FAFCFA]">
      <td className="px-4 py-2 text-[color:var(--color-text-muted)]">{label}</td>
      <td className={`px-4 py-2 text-right tabular-nums ${bold ? 'font-semibold' : 'font-medium'} text-[color:var(--color-text-primary)]`}>
        {value}
      </td>
    </tr>
  );
}

// ─────────────────────── Section 3: Financial Snapshot ────────────────────
export function FinancialSnapshotTable({ rows, testId = 'financial-snapshot' }) {
  return (
    <Section testId={testId} eyebrow="Section 03" title="Financial Snapshot"
             right={<span className="text-[11px] text-[#7A8B82]">Cost efficiency at portfolio scope</span>}>
      <div className="bg-white border border-[#E2E8DF] rounded-xl overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead className="bg-[#F7F9F7]">
            <tr className="text-[10px] uppercase tracking-widest text-[#5C6B64]">
              <th className="px-4 py-2.5 text-left font-semibold">Metric</th>
              <th className="px-4 py-2.5 text-right font-semibold">Current</th>
              <th className="px-4 py-2.5 text-right font-semibold">Target / Budget</th>
              <th className="px-4 py-2.5 text-right font-semibold">Variance</th>
              <th className="px-4 py-2.5 text-right font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0F4F1]">
            {rows.map((r) => (
              <FinSnapRow key={r.label} row={r} />
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function FinSnapRow({ row }) {
  const { label, currentText, targetText, variance, status, statusTone } = row;
  const toneCls =
    statusTone === 'good'    ? 'bg-emerald-50 text-emerald-700' :
    statusTone === 'warn'    ? 'bg-amber-50 text-amber-700' :
    statusTone === 'bad'     ? 'bg-rose-50 text-rose-700' :
                                'bg-[#F3F5F2] text-[#5C6B64]';
  return (
    <tr className="hover:bg-[#FAFCFA]">
      <td className="px-4 py-2.5 text-[color:var(--color-text-primary)]">{label}</td>
      <td className="px-4 py-2.5 text-right tabular-nums font-medium">{currentText || '—'}</td>
      <td className="px-4 py-2.5 text-right tabular-nums text-[color:var(--color-text-muted)]">{targetText || '—'}</td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {variance != null && variance !== ''
          ? <span className={statusTone === 'good' ? goodTxt : statusTone === 'bad' ? badTxt : ''}>{variance}</span>
          : <span className="text-[#9CA3AF]">—</span>}
      </td>
      <td className="px-4 py-2.5 text-right">
        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${toneCls}`}>
          {status || '—'}
        </span>
      </td>
    </tr>
  );
}

// ─────────────────────── Section 4: Rooms & Accommodation ────────────────────
export function RoomsAccommodationSection({
  mtdKpi, ytdKpi, testId = 'rooms-section',
}) {
  if (!mtdKpi && !ytdKpi) return null;

  const chartData = [];
  if (mtdKpi) chartData.push({ label: 'MTD Actual', value: mtdKpi.revenue, group: 'MTD' });
  if (mtdKpi) chartData.push({ label: 'MTD Budget', value: mtdKpi.budget, group: 'MTD' });
  if (ytdKpi) chartData.push({ label: 'YTD Actual', value: ytdKpi.revenue, group: 'YTD' });
  if (ytdKpi) chartData.push({ label: 'YTD Budget', value: ytdKpi.budget, group: 'YTD' });

  return (
    <Section testId={testId} eyebrow="Section 04" title="Rooms · Accommodation"
             right={<span className="text-[11px] text-[#7A8B82]">
               {(mtdKpi?.hotelOutlets || ytdKpi?.hotelOutlets || 0)} hotel outlets
             </span>}>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white border border-[#E2E8DF] rounded-xl overflow-hidden">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#F7F9F7] text-[10px] uppercase tracking-widest text-[#5C6B64]">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold">Metric</th>
                <th className="px-4 py-2.5 text-right font-semibold">Current Month · MTD</th>
                <th className="px-4 py-2.5 text-right font-semibold">Current Year · YTD</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F4F1]">
              <RoomRow label="Room Revenue"
                        mtd={mtdKpi ? compactINR(mtdKpi.revenue) : '—'}
                        ytd={ytdKpi ? compactINR(ytdKpi.revenue) : '—'} />
              <RoomRow label="Room Budget"
                        mtd={mtdKpi ? compactINR(mtdKpi.budget) : '—'}
                        ytd={ytdKpi ? compactINR(ytdKpi.budget) : '—'} />
              <RoomRow label="Variance vs Budget"
                        mtd={mtdKpi
                          ? <VarianceChip amount={mtdKpi.varAmt} pct={mtdKpi.varPct} compact />
                          : '—'}
                        ytd={ytdKpi
                          ? <VarianceChip amount={ytdKpi.varAmt} pct={ytdKpi.varPct} compact />
                          : '—'} />
              <RoomRow label="YoY Growth"
                        mtd={mtdKpi?.yoyPct != null
                          ? <span className={mtdKpi.yoyPct >= 0 ? goodTxt : badTxt}>{pctFmt(mtdKpi.yoyPct, { signed: true })}</span>
                          : '—'}
                        ytd={ytdKpi?.yoyPct != null
                          ? <span className={ytdKpi.yoyPct >= 0 ? goodTxt : badTxt}>{pctFmt(ytdKpi.yoyPct, { signed: true })}</span>
                          : '—'} />
              <RoomRow label="Occupancy"
                        mtd={mtdKpi?.occ != null ? pctFmt(mtdKpi.occ) : '—'}
                        ytd={ytdKpi?.occ != null ? pctFmt(ytdKpi.occ) : '—'} />
              <RoomRow label="ARR"
                        mtd={mtdKpi?.arr != null ? compactINR(mtdKpi.arr) : '—'}
                        ytd={ytdKpi?.arr != null ? compactINR(ytdKpi.arr) : '—'} />
              <RoomRow label="Rooms Sold"
                        mtd={mtdKpi?.rSold ? Number(mtdKpi.rSold).toLocaleString('en-IN') : '—'}
                        ytd={ytdKpi?.rSold ? Number(ytdKpi.rSold).toLocaleString('en-IN') : '—'} />
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-2 bg-white border border-[#E2E8DF] rounded-xl overflow-hidden">
          <div className="bg-[color:var(--color-primary)] text-white px-4 py-2 text-[11px] uppercase tracking-widest font-semibold">
            Actual vs Budget · Revenue Comparison
          </div>
          <div className="h-[220px] p-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid stroke="#EEF2EF" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#7A8B82', fontSize: 10 }}
                        axisLine={{ stroke: '#EEF2EF' }} tickLine={false} interval={0} />
                <YAxis tickFormatter={(v) => compactINR(v).replace('₹', '')}
                        tick={{ fill: '#7A8B82', fontSize: 10 }}
                        axisLine={false} tickLine={false} width={54} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 6,
                                    borderColor: '#E2E8DF' }}
                  formatter={(v) => compactINR(v)} />
                <Bar dataKey="value" fill="#2D6A4F" maxBarSize={28} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </Section>
  );
}

function RoomRow({ label, mtd, ytd }) {
  return (
    <tr className="hover:bg-[#FAFCFA]">
      <td className="px-4 py-2.5 text-[color:var(--color-text-muted)]">{label}</td>
      <td className="px-4 py-2.5 text-right tabular-nums font-medium text-[color:var(--color-text-primary)]">{mtd}</td>
      <td className="px-4 py-2.5 text-right tabular-nums font-medium text-[color:var(--color-text-primary)]">{ytd}</td>
    </tr>
  );
}

// ─────────────────────── Section 5: Outlet Pulse table ────────────────────
export function OutletPulseTable({ rows, testId = 'outlet-pulse-table' }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <Section testId={testId} eyebrow="Section 05" title="Outlet Pulse">
        <div className="bg-white border border-[#E2E8DF] rounded-xl p-10 text-center text-[13px] text-[#9CA3AF]">
          No outlets in scope
        </div>
      </Section>
    );
  }
  return (
    <Section testId={testId} eyebrow="Section 05" title="Outlet Pulse"
             right={<span className="text-[11px] text-[#7A8B82]">{rows.length} outlets</span>}>
      <div className="bg-white border border-[#E2E8DF] rounded-xl overflow-x-auto">
        <table className="w-full text-[12.5px] min-w-[860px]">
          <thead className="bg-[#F7F9F7] text-[10px] uppercase tracking-widest text-[#5C6B64]">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold">Outlet</th>
              <th className="px-3 py-2.5 text-right font-semibold">Revenue</th>
              <th className="px-3 py-2.5 text-right font-semibold">vs Budget</th>
              <th className="px-3 py-2.5 text-right font-semibold">APC</th>
              <th className="px-3 py-2.5 text-right font-semibold">Food Cost</th>
              <th className="px-3 py-2.5 text-right font-semibold">Bev Cost</th>
              <th className="px-3 py-2.5 text-right font-semibold">Energy</th>
              <th className="px-3 py-2.5 text-right font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0F4F1]">
            {rows.map((r) => <OutletRow key={r.key} row={r} />)}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function OutletRow({ row }) {
  const { name, code, revenue, varAmt, varPct, apc, fc, bc, energy, statusTone } = row;
  const status = row.status || (varPct == null ? 'No budget'
    : varPct >= 5   ? 'Above budget'
    : varPct >= -5  ? 'On track'
    :                 'Below budget');
  const toneCls =
    statusTone === 'good'    ? 'bg-emerald-50 text-emerald-700' :
    statusTone === 'ok'      ? 'bg-[#EFF3EE] text-[#4A7059]' :
    statusTone === 'bad'     ? 'bg-rose-50 text-rose-700' :
                                'bg-[#F3F5F2] text-[#5C6B64]';
  return (
    <tr className="hover:bg-[#FAFCFA]">
      <td className="px-3 py-2.5">
        <div className="font-medium text-[color:var(--color-text-primary)] truncate">{name}</div>
        {code && <div className="text-[10px] text-[#9CA3AF] font-mono">{code}</div>}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums font-medium text-[color:var(--color-text-primary)]">
        {compactINR(revenue)}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">
        {varPct == null
          ? <span className="text-[#9CA3AF]">—</span>
          : <span className={varPct >= 0 ? goodTxt : badTxt}>{pctFmt(varPct, { signed: true })}</span>}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-[color:var(--color-text-primary)]">
        {apc != null && apc > 0 ? compactINR(apc) : <span className="text-[#9CA3AF]">—</span>}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-[color:var(--color-text-primary)]">
        {fc != null ? pctFmt(fc) : <span className="text-[#9CA3AF]">—</span>}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-[color:var(--color-text-primary)]">
        {bc != null ? pctFmt(bc) : <span className="text-[#9CA3AF]">—</span>}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-[color:var(--color-text-primary)]">
        {energy != null ? compactINR(energy) : <span className="text-[#9CA3AF]">—</span>}
      </td>
      <td className="px-3 py-2.5 text-right">
        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${toneCls}`}>
          {status}
        </span>
      </td>
    </tr>
  );
}

// ─────────────────────── Section 6: Utilities / Energy ────────────────────
export function UtilitiesEnergySection({ rows, chartData, testId = 'utilities-energy' }) {
  const hasRows = Array.isArray(rows) && rows.length > 0;
  return (
    <Section testId={testId} eyebrow="Section 06" title="Utilities · Energy"
             right={<Zap className="w-3.5 h-3.5 text-[#7A8B82]" strokeWidth={2} />}>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white border border-[#E2E8DF] rounded-xl overflow-hidden">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#F7F9F7] text-[10px] uppercase tracking-widest text-[#5C6B64]">
              <tr>
                <th className="px-4 py-2.5 text-left font-semibold">Metric</th>
                <th className="px-4 py-2.5 text-right font-semibold">Actual</th>
                <th className="px-4 py-2.5 text-right font-semibold">Budget</th>
                <th className="px-4 py-2.5 text-right font-semibold">Variance</th>
                <th className="px-4 py-2.5 text-right font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F4F1]">
              {hasRows ? rows.map((r) => <FinSnapRow key={r.label} row={r} />)
                       : <tr><td className="px-4 py-6 text-center text-[#9CA3AF] italic" colSpan={5}>No utilities data reported</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="lg:col-span-2 bg-white border border-[#E2E8DF] rounded-xl overflow-hidden">
          <div className="bg-[color:var(--color-primary)] text-white px-4 py-2 text-[11px] uppercase tracking-widest font-semibold">
            Actual vs Budget
          </div>
          <div className="h-[220px] p-3">
            {Array.isArray(chartData) && chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#EEF2EF" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: '#7A8B82', fontSize: 10 }}
                          axisLine={{ stroke: '#EEF2EF' }} tickLine={false} />
                  <YAxis tickFormatter={(v) => compactINR(v).replace('₹', '')}
                          tick={{ fill: '#7A8B82', fontSize: 10 }}
                          axisLine={false} tickLine={false} width={54} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6, borderColor: '#E2E8DF' }}
                            formatter={(v) => compactINR(v)} />
                  <Legend iconType="rect" wrapperStyle={{ fontSize: 10 }} iconSize={10} />
                  <Bar dataKey="actual" name="Actual" fill="#2D6A4F" maxBarSize={22} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="budget" name="Budget" fill="#B0BFB6" maxBarSize={22} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-[12px] text-[#9CA3AF] italic">
                No utility trend
              </div>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}
